<template>
  <v-container fluid>
    <v-card class="mb-4 d-print-none" outlined>
      <v-card-title class="pb-0">
        Day Nutrition
        <span class="text-caption grey--text ml-3">
          each delivery date's planned day of meals, per diet, against the full nutrition targets (the RDAs)
          and the diet's own targets where they differ; and every day a customer could assemble by swapping meals between diets
        </span>
        <v-spacer/>
        <v-badge :content="issueCount" :value="issueCount > 0" color="orange darken-2" overlap>
          <v-btn small outlined @click="issuesDialog = true" data-cy="data-issues">
            <v-icon small left>mdi-alert-circle-outline</v-icon>
            Data issues
          </v-btn>
        </v-badge>
      </v-card-title>
      <v-card-actions class="flex-wrap">
        <v-btn class="ml-2 mb-2" icon @click="skipWeek(-1)" title="Previous week">
          <v-icon>mdi-chevron-left</v-icon>
        </v-btn>
        <date-picker class="ml-2" style="width: 300px" :dates.sync="dates"/>
        <v-btn class="ml-2 mb-2" icon @click="skipWeek(1)" title="Next week">
          <v-icon>mdi-chevron-right</v-icon>
        </v-btn>
        <v-select
          class="ml-4"
          style="max-width: 140px"
          v-model="size"
          :items="sizeItems"
          label="Meal size"
          dense
          hide-details
        />
        <v-autocomplete
          class="ml-4"
          style="min-width: 320px; max-width: 600px"
          v-model="ignored"
          :items="ignorableNutrients"
          item-text="label"
          item-value="symbol"
          label="Ignore targets"
          hint="targets left out of every score, here and in the swaps"
          persistent-hint
          multiple
          chips
          small-chips
          deletable-chips
          dense
          clearable
          data-cy="ignore-targets"
        />
      </v-card-actions>
      <v-card-actions class="flex-wrap">
        <v-select
          class="ml-2"
          style="max-width: 260px"
          v-model="judgeDietId"
          :items="judgeDietItems"
          label="Judge swaps against"
          dense
          hide-details
        />
        <v-select
          class="ml-4"
          style="min-width: 320px; max-width: 700px"
          v-model="poolDietIds"
          :items="dietItems"
          label="Diets in the swap pool"
          multiple
          chips
          small-chips
          deletable-chips
          dense
          hide-details
        />
        <v-autocomplete
          class="ml-4"
          style="min-width: 320px; max-width: 700px"
          v-model="filterIds"
          :items="filterItems"
          label="Diet presets and nutrition goals"
          hint="each planned day and every swap is also judged against these"
          persistent-hint
          multiple
          chips
          small-chips
          deletable-chips
          dense
          clearable
          data-cy="meal-filters"
        />
        <v-switch class="ml-4" v-model="showAllTargets" label="Show every target per diet" dense hide-details/>
        <v-switch
          class="ml-4"
          v-model="serverCheck"
          label="Server check"
          title="Also judge each planned day on the server, from the database (slow); differences from the menu-based scores are highlighted"
          dense
          hide-details
          data-cy="server-check"
        />
      </v-card-actions>
      <v-progress-linear v-if="loading || serverPending" :value="serverProgress" :indeterminate="loading" height="4"/>
      <v-card-text v-if="loading" class="text-caption">Loading the published menus…</v-card-text>
      <v-card-text v-else-if="serverPending" class="text-caption" data-cy="server-check-loading">
        Server check: {{ serverDone }} of {{ serverTotal }} planned days judged…
      </v-card-text>
    </v-card>

    <v-alert v-if="error" type="error" dense>{{ error }}</v-alert>
    <v-alert v-else-if="!loading && menusLoaded && meals.length === 0" type="info" text dense>
      No published menu has meals dated in this range.
    </v-alert>

    <!-- overview: every date against every diet -->
    <v-card v-if="dates.length && dayRows.length" class="mb-4" outlined data-cy="overview">
      <v-card-title class="text-subtitle-1">
        Targets met per planned day
        <span class="text-caption grey--text ml-3">
          RDA score first; the diet's own score follows where its targets differ. {{ ignored.length }} target{{ ignored.length === 1 ? '' : 's' }} ignored.
        </span>
      </v-card-title>
      <v-card-subtitle class="text-caption pt-0">
        Targets met out of targets judged.
        <v-chip x-small label dark color="green" class="mx-1">RDA</v-chip> filled: against the full nutrition targets;
        <v-chip x-small label outlined color="green" class="mx-1">diet</v-chip> outlined: against the diet's own targets.
        Colour is the share met:
        <v-chip x-small label dark color="green" class="ml-1">90%+</v-chip>
        <v-chip x-small label dark color="light-green darken-1" class="ml-1">75%+</v-chip>
        <v-chip x-small label dark color="orange" class="ml-1">50%+</v-chip>
        <v-chip x-small label dark color="red" class="ml-1">under 50%</v-chip>
        <v-chip x-small label dark color="grey" class="ml-1">?</v-chip> no nutrient data.
        <div class="mt-1">
          <b>Closeness</b> is how far a day gets to each target, averaged: a minimum reached counts 100%, one half reached 50%;
          a maximum kept to counts 100%, one exceeded by a quarter 80%. Hover a chip for a day's closeness.
        </div>
      </v-card-subtitle>
      <v-simple-table dense>
        <thead>
        <tr>
          <th>Date</th>
          <th v-for="diet of diets" :key="diet.id">{{ diet.name }}</th>
        </tr>
        </thead>
        <tbody>
        <tr v-for="day of dayRows" :key="day.date">
          <td><a :href="`#date-${day.date}`">{{ day.date }}</a></td>
          <td v-for="diet of diets" :key="diet.id">
            <template v-if="day.byDiet[diet.id]">
              <v-chip x-small label :color="scoreColour(day.byDiet[diet.id].rda.score)" dark :title="metTitle(day.byDiet[diet.id].rda)" :data-cy="`rda-${day.date}-${diet.id}`">
                {{ scoreText(day.byDiet[diet.id].rda) }}
              </v-chip>
              <v-chip
                v-if="day.byDiet[diet.id].own"
                x-small label outlined class="ml-1"
                :color="scoreColour(day.byDiet[diet.id].own.score)"
                :title="`${diet.name} targets: ` + metTitle(day.byDiet[diet.id].own)"
              >
                {{ scoreText(day.byDiet[diet.id].own) }}
              </v-chip>
              <span v-if="!day.byDiet[diet.id].rda.complete" class="text-caption grey--text ml-1" title="fewer than three meals; targets scaled">partial</span>
              <v-icon
                v-if="serverCheck && serverVerdictDiffers(day.date, day.byDiet[diet.id])"
                x-small color="red" class="ml-1" title="the server check disagrees; see the date below"
              >
                mdi-alert
              </v-icon>
            </template>
            <span v-else class="grey--text">—</span>
          </td>
        </tr>
        </tbody>
        <tfoot>
        <tr>
          <th>average RDA score</th>
          <th v-for="diet of diets" :key="diet.id">{{ averageScore(diet.id) }}</th>
        </tr>
        <tr>
          <td class="text-caption"><b>average closeness</b></td>
          <td v-for="diet of diets" :key="`closeness-${diet.id}`" class="text-caption">{{ averageCloseness(diet.id) }}</td>
        </tr>
        </tfoot>
      </v-simple-table>
    </v-card>

    <!-- overview: presets and goals, every diet's days -->
    <v-card v-if="dates.length && dayRows.length && selectedFilters.length" class="mb-4" outlined data-cy="filters-overview">
      <v-card-title class="text-subtitle-1">
        Diet presets and nutrition goals per diet
        <span class="text-caption grey--text ml-3">
          days of the range meeting the filter, and the days' average closeness; a filter's limits are a day's, its restrictions apply to every meal
        </span>
      </v-card-title>
      <v-simple-table dense>
        <thead>
        <tr>
          <th>Filter</th>
          <th v-for="diet of diets" :key="diet.id">{{ diet.name }}</th>
        </tr>
        </thead>
        <tbody>
        <tr v-for="(filter, index) of selectedFilters" :key="filter.id">
          <td class="text-no-wrap" :title="filterDescription(filter)">{{ filterLabels[index] }}</td>
          <td v-for="diet of diets" :key="diet.id" class="text-no-wrap">
            <template v-if="filterDays(diet.id, index).judged">
              <v-chip x-small label dark :color="scoreColour(filterDays(diet.id, index).metPercent)">
                {{ filterDays(diet.id, index).met }}/{{ filterDays(diet.id, index).judged }} days
              </v-chip>
              <span class="text-caption grey--text ml-1">{{ Math.round(filterDays(diet.id, index).closeness) }}%</span>
            </template>
            <span v-else class="grey--text">—</span>
          </td>
        </tr>
        </tbody>
      </v-simple-table>
    </v-card>

    <!-- one card per date -->
    <v-card v-for="day of dayRows" :key="day.date" :id="`date-${day.date}`" class="mb-6" outlined>
      <v-card-title class="text-subtitle-1">{{ formatDate(day.date) }}</v-card-title>

      <v-simple-table dense class="planned-table">
        <thead>
        <tr>
          <th>Diet</th>
          <th>Breakfast</th>
          <th>Lunch</th>
          <th>Dinner</th>
          <th>RDA met</th>
          <th>Diet targets met</th>
          <th title="how far the day gets to each target, averaged">Closeness</th>
          <th v-if="serverCheck">Server</th>
          <th v-if="selectedFilters.length">Presets and goals</th>
          <th>Issues</th>
        </tr>
        </thead>
        <tbody>
        <template v-for="diet of diets">
          <tr v-if="day.byDiet[diet.id]" :key="diet.id" :data-cy="`planned-${day.date}-${diet.id}`">
            <td class="text-no-wrap">
              <b>{{ diet.name }}</b>
              <div v-if="!day.byDiet[diet.id].rda.complete" class="text-caption grey--text">{{ day.byDiet[diet.id].meals.length }} meals, targets scaled</div>
            </td>
            <td v-for="tod of [1, 2, 3]" :key="tod" class="meal-cell">
              <template v-for="meal of mealsAt(day.byDiet[diet.id].meals, tod)">
                <router-link :key="meal.id" :to="mealLink(meal)">{{ meal.name }}</router-link>
              </template>
            </td>
            <td class="text-no-wrap">
              <v-chip x-small label dark :color="scoreColour(day.byDiet[diet.id].rda.score)">{{ scoreText(day.byDiet[diet.id].rda) }}</v-chip>
            </td>
            <td class="text-no-wrap">
              <v-chip v-if="day.byDiet[diet.id].own" x-small label outlined :color="scoreColour(day.byDiet[diet.id].own.score)">
                {{ scoreText(day.byDiet[diet.id].own) }}
              </v-chip>
              <span v-else-if="diet.id === FULL_NUTRITION_DIET" class="text-caption grey--text">the RDAs</span>
              <span v-else-if="!dietHasTargets(diet.id)" class="text-caption grey--text">no targets</span>
              <span v-else class="text-caption grey--text">same as RDA</span>
            </td>
            <td class="text-no-wrap text-caption" :data-cy="`closeness-${day.date}-${diet.id}`">
              <template v-if="day.byDiet[diet.id].rda.judged">
                {{ Math.round(day.byDiet[diet.id].rda.closeness) }}%
                <span v-if="day.byDiet[diet.id].own" class="grey--text" :title="`${diet.name} targets`">({{ Math.round(day.byDiet[diet.id].own.closeness) }}%)</span>
              </template>
            </td>
            <td v-if="serverCheck" class="text-no-wrap">
              <template v-if="serverCheckFor(day.date, day.byDiet[diet.id])">
                <span v-if="serverCheckFor(day.date, day.byDiet[diet.id]).pending" class="text-caption grey--text">judging…</span>
                <span v-else-if="serverCheckFor(day.date, day.byDiet[diet.id]).error" class="text-caption red--text" :title="serverCheckFor(day.date, day.byDiet[diet.id]).error">server error</span>
                <v-tooltip v-else bottom max-width="520">
                  <template v-slot:activator="{on}">
                    <v-chip
                      x-small label v-on="on"
                      :dark="serverComparison(day.date, day.byDiet[diet.id]).differences.length > 0"
                      :outlined="serverComparison(day.date, day.byDiet[diet.id]).differences.length === 0"
                      :color="serverChipColour(serverComparison(day.date, day.byDiet[diet.id]))"
                      :data-cy="`server-${day.date}-${diet.id}`"
                    >
                      {{ serverChipText(serverComparison(day.date, day.byDiet[diet.id])) }}
                    </v-chip>
                  </template>
                  <div class="text-caption">
                    <div v-if="serverComparison(day.date, day.byDiet[diet.id]).differences.length === 0">
                      The server judged the same {{ serverComparison(day.date, day.byDiet[diet.id]).compared }} targets from the database and agrees.
                    </div>
                    <template v-else>
                      <div class="mb-1">
                        The server judged from the database; this page judges from the published menu. A difference
                        usually means the menu was published before a meal's recipe changed: republish the menu.
                      </div>
                      <div v-for="difference of serverComparison(day.date, day.byDiet[diet.id]).differences" :key="difference.nutrient_id">
                        <b>{{ difference.name }}</b>:
                        menu {{ amount(difference.clientAmount, difference) }} ({{ difference.clientMet ? 'met' : 'failed' }}),
                        server {{ amount(difference.serverAmount, difference) }} ({{ difference.serverMet ? 'met' : 'failed' }})
                      </div>
                    </template>
                    <div v-if="serverComparison(day.date, day.byDiet[diet.id]).notCompared" class="grey--text">
                      {{ serverComparison(day.date, day.byDiet[diet.id]).notCompared }} target(s) the server returned no amount for were not compared.
                    </div>
                  </div>
                </v-tooltip>
              </template>
              <span v-else class="text-caption grey--text" title="only complete days of a diet with targets are checked on the server">—</span>
            </td>
            <td v-if="selectedFilters.length" :data-cy="`filters-${day.date}-${diet.id}`">
              <v-tooltip v-for="judgement of day.byDiet[diet.id].filters" :key="judgement.filter.id" bottom max-width="520">
                <template v-slot:activator="{on}">
                  <v-chip
                    x-small label class="ma-1" v-on="on"
                    :dark="judgement.judged && !judgement.met"
                    :outlined="!judgement.judged || judgement.met"
                    :color="judgement.judged ? (judgement.met ? 'green' : 'red') : 'grey'"
                  >
                    {{ judgement.label }}<template v-if="judgement.judged"> {{ Math.round(judgement.closeness) }}%</template>
                  </v-chip>
                </template>
                <div class="text-caption">
                  <div v-if="!judgement.judged">The menu carries no nutrients for a meal of this day.</div>
                  <template v-else>
                    <div class="mb-1"><b>{{ judgement.label }}</b>: {{ judgement.met ? 'met' : 'not met' }}, closeness {{ Math.round(judgement.closeness) }}%</div>
                    <div v-for="result of judgement.limits.results" :key="result.nutrient_id" :class="result.met ? '' : 'orange--text text--lighten-2'">
                      {{ result.name }}: {{ amount(result.amount, result) }} of {{ limits(result) }} ({{ Math.round(result.closeness) }}%)
                    </div>
                    <div v-for="entry of judgement.restricted.broken" :key="entry.id" class="orange--text text--lighten-2">
                      {{ entry.text }}: <span v-for="(hit, i) of entry.meals" :key="hit.meal.id"><span v-if="i">, </span>{{ hit.meal.name }} ({{ hit.tags.join(', ') }})</span>
                    </div>
                    <div v-for="id of judgement.filter.restrictions || []" :key="`ok-${id}`">
                      <template v-if="!judgement.restricted.broken.some(entry => entry.id === id) && !judgement.restricted.unknown.includes(id)">{{ restrictionText(id) }}: every meal clear</template>
                      <template v-else-if="judgement.restricted.unknown.includes(id)"><span class="grey--text">{{ id }}: no restriction definition, not checked</span></template>
                    </div>
                  </template>
                </div>
              </v-tooltip>
            </td>
            <td>
              <template v-if="day.byDiet[diet.id].rda.judged">
                <v-chip
                  v-for="failure of day.byDiet[diet.id].rda.failures"
                  :key="`rda-${failure.nutrient_id}`"
                  x-small class="ma-1" :color="problemColour(failure)" outlined
                  :title="describe(failure)"
                >
                  {{ shortFailure(failure) }}
                </v-chip>
                <template v-if="day.byDiet[diet.id].own">
                  <v-chip
                    v-for="failure of ownOnlyFailures(day.byDiet[diet.id])"
                    :key="`own-${failure.nutrient_id}`"
                    x-small class="ma-1" :color="problemColour(failure)"
                    :title="`${diet.name} target: ` + describe(failure)"
                  >
                    {{ diet.name }}: {{ shortFailure(failure) }}
                  </v-chip>
                </template>
                <span v-if="!day.byDiet[diet.id].rda.failures.length && !(day.byDiet[diet.id].own && day.byDiet[diet.id].own.failures.length)" class="green--text text-caption">
                  every target met
                </span>
              </template>
              <span v-else class="text-caption grey--text">the menu carries no nutrients for a meal of this day</span>
            </td>
          </tr>
          <tr v-if="showAllTargets && day.byDiet[diet.id] && day.byDiet[diet.id].rda.judged" :key="`${diet.id}-detail`" class="detail-row">
            <td :colspan="plannedColumns">
              <v-simple-table dense class="detail-table">
                <thead>
                <tr>
                  <th>Nutrient</th>
                  <th class="text-right">Amount</th>
                  <th class="text-right">RDA target</th>
                  <th class="text-right">% of RDA</th>
                  <th>RDA limits</th>
                  <th v-if="day.byDiet[diet.id].own" class="text-right">{{ diet.name }} target</th>
                  <th v-if="day.byDiet[diet.id].own">{{ diet.name }} limits</th>
                  <th class="text-right">Closeness</th>
                  <th>Status</th>
                </tr>
                </thead>
                <tbody>
                <tr v-for="row of detailRows(day.byDiet[diet.id])" :key="row.nutrient_id" :class="{'red lighten-5': !row.met}">
                  <td>{{ row.name }} <span class="grey--text">{{ row.symbol }}</span></td>
                  <td class="text-right">{{ amount(row.amount, row) }}</td>
                  <td class="text-right">{{ amount(row.target, row) }}</td>
                  <td class="text-right">{{ row.percent === null ? '' : Math.round(row.percent) + '%' }}</td>
                  <td class="text-caption">{{ limits(row) }}</td>
                  <td v-if="day.byDiet[diet.id].own" class="text-right">{{ row.own ? amount(row.own.target, row) : '' }}</td>
                  <td v-if="day.byDiet[diet.id].own" class="text-caption">{{ row.own ? limits(row.own) : '' }}</td>
                  <td class="text-right">{{ row.closeness === null || row.closeness === undefined ? '' : Math.round(row.closeness) + '%' }}<span v-if="row.own" class="grey--text"> ({{ Math.round(row.own.closeness) }}%)</span></td>
                  <td class="text-caption">
                    <span :class="row.met ? 'green--text' : 'red--text'">{{ row.problems.length ? row.problems.join(', ') : 'met' }}</span>
                    <span v-if="row.own && !row.own.met" class="red--text"> · {{ diet.name }}: {{ row.own.problems.join(', ') }}</span>
                  </td>
                </tr>
                </tbody>
              </v-simple-table>
            </td>
          </tr>
        </template>
        </tbody>
      </v-simple-table>

      <!-- swaps -->
      <v-divider class="my-2"/>
      <v-card-subtitle class="pb-1">
        <b>Swapping meals between diets</b>
        <span class="text-caption grey--text ml-2">
          a breakfast with any other breakfast, a lunch or dinner with any other lunch or dinner, from
          {{ day.swaps.poolNames.join(', ') || 'no diets' }}; judged against {{ judgeDietName }} targets
        </span>
      </v-card-subtitle>
      <v-card-text v-if="!day.swaps.summary.count" class="text-caption grey--text">
        {{ day.swaps.count ? 'The menu carries no nutrients for the meals in the pool.' : 'Not enough meals in the pool to assemble a day.' }}
      </v-card-text>
      <v-row v-else dense class="px-4 pb-2" :data-cy="`swaps-${day.date}`">
        <v-col cols="12" md="4">
          <div class="text-body-2">
            <b>{{ day.swaps.summary.count.toLocaleString() }}</b> {{ day.swaps.sampled ? 'random' : 'possible' }} days
            <span v-if="day.swaps.sampled" class="grey--text">(of {{ day.swaps.count.toLocaleString() }})</span>
          </div>
          <v-simple-table dense class="bounds-table">
            <tbody>
            <tr>
              <td>worst day</td>
              <td class="text-right"><v-chip x-small label dark :color="scoreColour(pct(day.swaps.summary.min, day.swaps.summary.total))">{{ day.swaps.summary.min }} / {{ day.swaps.summary.total }}</v-chip></td>
            </tr>
            <tr>
              <td>median</td>
              <td class="text-right">{{ day.swaps.summary.median }} / {{ day.swaps.summary.total }}</td>
            </tr>
            <tr>
              <td>mean</td>
              <td class="text-right">{{ day.swaps.summary.mean.toFixed(1) }} / {{ day.swaps.summary.total }}</td>
            </tr>
            <tr>
              <td>best day</td>
              <td class="text-right"><v-chip x-small label dark :color="scoreColour(pct(day.swaps.summary.max, day.swaps.summary.total))">{{ day.swaps.summary.max }} / {{ day.swaps.summary.total }}</v-chip></td>
            </tr>
            <tr>
              <td>meeting every target</td>
              <td class="text-right">{{ perfectShare(day.swaps.summary) }}</td>
            </tr>
            <tr v-if="day.swaps.summary.closeness">
              <td>closeness</td>
              <td class="text-right" :title="'mean closeness of the days, and the lowest and highest'">
                {{ Math.round(day.swaps.summary.closeness.mean) }}%
                <span class="grey--text">({{ Math.round(day.swaps.summary.closeness.min) }}% – {{ Math.round(day.swaps.summary.closeness.max) }}%)</span>
              </td>
            </tr>
            </tbody>
          </v-simple-table>
          <div class="text-caption mt-3 mb-1"><b>Targets failed, by share of days</b></div>
          <div v-for="failure of day.swaps.summary.failureCounts" :key="failure.nutrient_id" class="text-caption">
            <span :style="{display: 'inline-block', width: '48px'}">{{ Math.round(failure.percent) }}%</span>
            {{ failure.name }}
            <span class="grey--text">{{ problemSummary(failure) }}</span>
          </div>
        </v-col>
        <v-col cols="12" md="4">
          <targets-met-chart :distribution="day.swaps.summary.distribution" :planned="day.swaps.planned"/>
          <div class="text-caption grey--text text-center">blue: a planned day's score</div>
        </v-col>
        <v-col cols="12" md="4">
          <div class="text-caption mb-1"><b>Worst days</b></div>
          <div v-for="(combo, index) of day.swaps.worst" :key="`worst-${index}`" class="text-caption mb-1">
            <v-chip x-small label dark :color="scoreColour(combo.score)" class="mr-1" :title="`closeness ${Math.round(combo.closeness)}%`">{{ combo.met }}/{{ combo.total }}</v-chip>
            <span v-for="(meal, i) of combo.meals" :key="meal.id">
              <span v-if="i">+ </span><router-link :to="mealLink(meal)">{{ meal.name }}</router-link>
              <span class="grey--text">({{ dietName(meal.diet) }}) </span>
            </span>
            <div class="grey--text pl-1">{{ combo.failures.map(shortFailure).join(', ') }}</div>
          </div>
          <div class="text-caption mt-3 mb-1"><b>Best days</b></div>
          <div v-for="(combo, index) of day.swaps.best" :key="`best-${index}`" class="text-caption mb-1">
            <v-chip x-small label dark :color="scoreColour(combo.score)" class="mr-1" :title="`closeness ${Math.round(combo.closeness)}%`">{{ combo.met }}/{{ combo.total }}</v-chip>
            <span v-for="(meal, i) of combo.meals" :key="meal.id">
              <span v-if="i">+ </span><router-link :to="mealLink(meal)">{{ meal.name }}</router-link>
              <span class="grey--text">({{ dietName(meal.diet) }}) </span>
            </span>
            <div v-if="combo.failures.length" class="grey--text pl-1">{{ combo.failures.map(shortFailure).join(', ') }}</div>
          </div>
        </v-col>
      </v-row>
      <template v-if="day.swaps.summary.count && day.swaps.filters.length">
        <v-divider class="my-2"/>
        <v-card-subtitle class="pb-1">
          <b>Diet presets and nutrition goals across the swaps</b>
          <span class="text-caption grey--text ml-2">how many of the {{ day.swaps.summary.count.toLocaleString() }} days meet each, their closeness, and what fails most</span>
        </v-card-subtitle>
        <v-simple-table dense class="mx-4 mb-3 filters-table" :data-cy="`swap-filters-${day.date}`">
          <thead>
          <tr>
            <th>Filter</th>
            <th class="text-right">Days meeting</th>
            <th class="text-right">Closeness mean (lowest – highest)</th>
            <th>Planned days</th>
            <th>Fails most</th>
          </tr>
          </thead>
          <tbody>
          <tr v-for="entry of day.swaps.filters" :key="entry.filter.id">
            <td class="text-no-wrap">{{ entry.label }}</td>
            <td class="text-right text-no-wrap">
              <template v-if="entry.summary.count">
                <v-chip x-small label dark :color="scoreColour(entry.summary.metPercent)">{{ Math.round(entry.summary.metPercent) }}%</v-chip>
                <span class="text-caption grey--text ml-1">{{ entry.summary.met.toLocaleString() }}</span>
              </template>
              <span v-else class="grey--text">—</span>
            </td>
            <td class="text-right text-no-wrap">
              <template v-if="entry.summary.closeness">
                {{ Math.round(entry.summary.closeness.mean) }}%
                <span class="grey--text">({{ Math.round(entry.summary.closeness.min) }}% – {{ Math.round(entry.summary.closeness.max) }}%)</span>
              </template>
            </td>
            <td class="text-caption">
              <span v-for="(planned, i) of entry.planned" :key="planned.label">
                <span v-if="i">, </span>
                <span :class="planned.met ? 'green--text' : 'red--text'">{{ planned.label }} {{ Math.round(planned.closeness) }}%</span>
              </span>
            </td>
            <td class="text-caption">
              <span v-for="(failure, i) of entry.summary.failureCounts.slice(0, 4)" :key="failure.name">
                <span v-if="i">, </span>{{ failure.name }} <span class="grey--text">{{ Math.round(failure.percent) }}%</span>
              </span>
            </td>
          </tr>
          </tbody>
        </v-simple-table>
      </template>
    </v-card>

    <!-- data issues -->
    <v-dialog v-model="issuesDialog" max-width="760" scrollable>
      <v-card>
        <v-card-title>
          Data issues
          <v-spacer/>
          <v-btn icon @click="issuesDialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-card-text class="text-body-2" data-cy="data-issues-dialog">
          <p class="grey--text text-caption">
            What this page reads and where its numbers can mislead. Scores come from the published menu's nutrients
            (one request a week) and the Nutrient Targets page's targets; nothing here is calculated on the server unless
            the server check is on.
          </p>

          <h4>Targets stored as a minimum that read like a maximum</h4>
          <p v-if="likelyLimits.length === 0" class="green--text">None in the full nutrition targets.</p>
          <template v-else>
            <p>
              A target value is a minimum to reach: a day is marked <i>under</i> until the amount reaches it. These full
              nutrition targets have a target value and no upper limit, yet the nutrient is one a day should stay <b>below</b>,
              so a day with little of it fails and one with too much passes. The existing per-meal analysis reads them the
              same way. The fix is on the Nutrient Targets page: move the value into the upper limit column. Until then,
              ignore them here.
            </p>
            <ul class="mb-3">
              <li v-for="target of likelyLimits" :key="target.nutrient_id">
                {{ target.name }}: target {{ amount(target.target, target) }}, no upper limit
                <span v-if="ignored.includes(target.symbol)" class="grey--text">(ignored)</span>
              </li>
            </ul>
            <v-btn small outlined color="orange darken-2" class="mb-4" @click="ignoreLikelyLimits" :disabled="likelyLimits.every(target => ignored.includes(target.symbol))">
              Ignore these targets
            </v-btn>
          </template>

          <h4>Targets no meal has data for</h4>
          <p v-if="untracked.length === 0" class="green--text">Every target's nutrient appears in the menu's meals.</p>
          <template v-else>
            <p>
              The published menu carries no amount of these nutrients for any meal in the range, so every day fails them
              (<i>no data</i>). Either the ingredients lack the nutrient in the database or the menu was published before
              the nutrient was tracked.
            </p>
            <ul class="mb-3">
              <li v-for="target of untracked" :key="target.nutrient_id">
                {{ target.name }} <span class="grey--text">{{ target.symbol || 'no symbol' }}</span>
                <span v-if="ignored.includes(target.symbol)" class="grey--text">(ignored)</span>
              </li>
            </ul>
          </template>

          <h4>Meals the menu carries no nutrients for</h4>
          <p v-if="mealsWithoutNutrients.length === 0" class="green--text">Every meal in the range has nutrients for the {{ size }} size.</p>
          <template v-else>
            <p>A day with one of these is not scored, and the meal is left out of the swaps. Republishing the menu for that week fills them in.</p>
            <ul class="mb-3">
              <li v-for="meal of mealsWithoutNutrients" :key="`${meal.diet}-${meal.id}-${meal.date}`">
                {{ meal.date }} {{ dietName(meal.diet) }}: <router-link :to="mealLink(meal)">{{ meal.name }}</router-link>
              </li>
            </ul>
          </template>

          <h4>Menu streams with no diet</h4>
          <p v-if="streamsWithoutDiet.length === 0" class="green--text">Every stream in the menus is assigned a diet.</p>
          <template v-else>
            <p>These streams are in the published menu but no diet on the Nutrient Targets page names them, so they have no targets and are left out.</p>
            <ul class="mb-3"><li v-for="stream of streamsWithoutDiet" :key="stream">{{ stream }}</li></ul>
          </template>

          <h4>Incomplete days</h4>
          <p v-if="incompleteDays.length === 0" class="green--text">Every diet has a breakfast, lunch and dinner on every date.</p>
          <template v-else>
            <p>These diets have fewer than three meals on the date, so the targets are scaled to the meals present and the score is marked <i>partial</i>.</p>
            <ul class="mb-3">
              <li v-for="day of incompleteDays" :key="`${day.date}-${day.dietId}`">{{ day.date }} {{ day.dietName }}: {{ day.count }} meal{{ day.count === 1 ? '' : 's' }}</li>
            </ul>
          </template>

          <h4>Diet presets and nutrition goals</h4>
          <p>
            A preset or goal is a meal filter: day limits per nutrient (the limits the Meal Filter List page holds, read as a
            day's over three meals) and restrictions on the foods of every meal. This page judges a day's summed nutrients
            against the limits; the menu generator judges each meal on its own, scaled to a day, so a day can pass here
            while one of its meals fails there. A filter's target value is ignored, as the generator ignores it.
          </p>
          <p v-if="!selectedFilters.length" class="grey--text">No preset or goal is selected.</p>
          <template v-else>
            <p v-if="filterIssues.length === 0" class="green--text">Every selected filter's nutrients appear in the menu's meals and every restriction is defined.</p>
            <ul v-else class="mb-3">
              <li v-for="issue of filterIssues" :key="issue.key">{{ issue.text }}</li>
            </ul>
          </template>

          <h4>Published menu versus the database</h4>
          <p>
            The menu's nutrients are fixed when it is published. A recipe edited since then is scored here as it was
            published. Turn on the server check to judge each planned day from the database as well; a day where the
            two disagree is flagged, and republishing the menu brings them back in line.
          </p>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import moment from 'moment';
import {mapActions, mapGetters, mapState} from 'vuex';
import api from '@/api';
import urlState from '@/router/urlState';
import DatePicker from '@/components/DatePicker';
import TargetsMetChart from '@/components/charts/TargetsMetChart';
import {
  FULL_NUTRITION_DIET,
  JUDGED_SIZE,
  compareWithServer,
  describeFailure,
  dietTargets,
  differingTargets,
  filterOptions,
  filterSwapSummary,
  filterTargets,
  groupMeals,
  judgeCombos,
  judgeFilter,
  judgeMeals,
  menuMeals,
  restrictedMeals,
  swapCombos,
  swapSummary,
  targetsDiffer,
  targetsLikelyLimits,
  untrackedTargets,
} from '@/utils/day-nutrition';

const IGNORED_KEY = 'day-nutrition-ignore-v1';
const FILTERS_KEY = 'day-nutrition-filters-v1';
const SHOWN_COMBOS = 5;
// server checks (GET meals/nutrients, one a planned day) a few at a time
const SERVER_CHECK_CONCURRENCY = 3;

const asList = value => (value === null || value === undefined || value === '' ? [] : Array.isArray(value) ? value : [value]);
const asNumbers = value => asList(value).map(Number).filter(n => !Number.isNaN(n));

function readStoredList(key) {
  try {
    const stored = JSON.parse(localStorage.getItem(key) || '[]');
    return Array.isArray(stored) ? stored : [];
  } catch (e) {
    return [];
  }
}

function storeList(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (e) {
    // the lists are small; nothing to do if storage is unavailable
  }
}

export default {
  name: 'DayNutrition',
  components: {DatePicker, TargetsMetChart},
  mixins: [urlState],
  data() {
    return {
      FULL_NUTRITION_DIET,
      loading: false,
      menusLoaded: false,
      error: null,
      dates: this.weekFrom(moment().add(7, 'days')),
      size: JUDGED_SIZE,
      sizeItems: ['small', 'medium', 'large'],
      ignored: readStoredList(IGNORED_KEY),
      // the diet presets and nutrition goals (meal filter ids) every day is also judged against
      filterIds: readStoredList(FILTERS_KEY),
      mealFilters: [],
      judgeDietId: FULL_NUTRITION_DIET,
      poolDietIds: null,
      showAllTargets: false,
      issuesDialog: false,
      // the published menus' meals in the range, each with its nutrients by size
      meals: [],
      streamsWithoutDiet: [],
      targetRows: [],
      // the server's judgement of planned days, by date, diet and meals: {pending, rows, error}
      serverCheck: false,
      serverChecks: {},
    };
  },
  mounted() {
    // a single date (the menu's week link, or one picked) means the week from that date
    this.syncToUrl({
      param: 'dates', urlParam: 'dates', initFromRoute: true,
      parseCallback: value => {
        const list = asList(value);
        return list.length === 1 ? this.weekFrom(moment(list[0])) : list;
      },
    });
    this.syncToUrl({param: 'size', urlParam: 'size', initFromRoute: true});
    this.syncToUrl({
      param: 'ignored', urlParam: 'ignore', initFromRoute: true,
      parseCallback: asList, transformCallback: value => (value && value.length ? value : null),
    });
    this.syncToUrl({
      param: 'filterIds', urlParam: 'filters', initFromRoute: true,
      parseCallback: asList, transformCallback: value => (value && value.length ? value : null),
    });
    this.syncToUrl({param: 'judgeDietId', urlParam: 'judge', initFromRoute: true, parseCallback: Number});
    this.syncToUrl({
      param: 'poolDietIds', urlParam: 'pool', initFromRoute: true,
      parseCallback: asNumbers, transformCallback: value => (value && value.length ? value.map(String) : null),
    });
    this.syncToUrl({
      param: 'serverCheck', urlParam: 'server', initFromRoute: true,
      parseCallback: value => value === 'true' || value === true, transformCallback: value => (value ? 'true' : null),
    });
    this.fetchData();
  },
  watch: {
    dates() {
      this.fetchData();
    },
    ignored(value) {
      storeList(IGNORED_KEY, value);
    },
    filterIds(value) {
      storeList(FILTERS_KEY, value);
    },
    serverCheck() {
      this.runServerChecks();
    },
    plannedDayKeys() {
      this.runServerChecks();
    },
  },
  computed: {
    ...mapState({nutrients: 'nutrients', dietsById: 'diets', restrictions: 'restrictions'}),
    ...mapGetters(['getDietName']),
    /** the diets with meals in the range, full nutrition first */
    diets() {
      const ids = [...new Set(this.meals.map(meal => meal.diet).filter(Boolean))];
      return ids
        .map(id => ({id, name: this.getDietName(id)}))
        .sort((a, b) => (a.id === FULL_NUTRITION_DIET ? -1 : b.id === FULL_NUTRITION_DIET ? 1 : a.name.localeCompare(b.name)));
    },
    dietItems() {
      return this.diets.map(diet => ({text: diet.name, value: diet.id}));
    },
    dietIdsWithTargets() {
      return [...new Set(this.targetRows.map(row => Number(row.diet)))];
    },
    judgeDietItems() {
      return this.dietIdsWithTargets
        .map(id => ({value: id, text: id === FULL_NUTRITION_DIET ? `${this.getDietName(id)} (RDA)` : this.getDietName(id)}))
        .sort((a, b) => (a.value === FULL_NUTRITION_DIET ? -1 : b.value === FULL_NUTRITION_DIET ? 1 : a.text.localeCompare(b.text)));
    },
    judgeDietName() {
      return this.getDietName(this.judgeDietId);
    },
    /** every nutrient any diet has a target for */
    ignorableNutrients() {
      const byId = {};
      for (const row of this.targetRows) {
        const nutrient = (this.nutrients || {})[row.nutrient_id];
        if (nutrient && nutrient.nutrientsymbol) byId[row.nutrient_id] = nutrient;
      }
      return Object.values(byId)
        .map(nutrient => ({symbol: nutrient.nutrientsymbol, label: `${nutrient.nutrientname} (${nutrient.nutrientsymbol})`}))
        .sort((a, b) => a.label.localeCompare(b.label));
    },
    rdaTargets() {
      return dietTargets(this.targetRows, FULL_NUTRITION_DIET, this.nutrients || {}, this.ignored);
    },
    /** each diet's own targets, only when they differ from the RDAs */
    ownTargetsByDiet() {
      const result = {};
      for (const diet of this.diets) {
        if (diet.id === FULL_NUTRITION_DIET) continue;
        const targets = dietTargets(this.targetRows, diet.id, this.nutrients || {}, this.ignored);
        if (targets.length && targetsDiffer(targets, this.rdaTargets)) result[diet.id] = targets;
      }
      return result;
    },
    judgeTargets() {
      return dietTargets(this.targetRows, this.judgeDietId, this.nutrients || {}, this.ignored);
    },
    poolDiets() {
      return this.poolDietIds === null ? this.diets.map(diet => diet.id) : this.poolDietIds;
    },
    filterItems() {
      return filterOptions(this.mealFilters);
    },
    /** the chosen filters, in the order chosen; an id no longer defined is skipped */
    selectedFilters() {
      return this.filterIds.map(id => this.mealFilters.find(filter => filter.id === id)).filter(Boolean);
    },
    filterLabels() {
      return this.selectedFilters.map(filter => this.filterItems.find(item => item.value === filter.id).text);
    },
    plannedColumns() {
      return 8 + (this.serverCheck ? 1 : 0) + (this.selectedFilters.length ? 1 : 0);
    },
    /** the judged size's nutrients by meal id, from the published menu */
    nutrientsByMealId() {
      const result = {};
      for (const meal of this.meals) {
        const nutrients = meal.nutrients && meal.nutrients[this.size];
        if (nutrients && !result[meal.id]) result[meal.id] = nutrients;
      }
      return result;
    },
    /** one entry per date: each diet's planned day judged, and the swaps */
    dayRows() {
      const grouped = groupMeals(this.meals);
      return Object.keys(grouped).sort().map(date => {
        const byDiet = {};
        const planned = [];
        for (const [dietId, meals] of Object.entries(grouped[date])) {
          const rda = judgeMeals(meals, this.nutrientsByMealId, this.rdaTargets);
          const ownTargets = this.ownTargetsByDiet[dietId];
          const own = ownTargets ? judgeMeals(meals, this.nutrientsByMealId, ownTargets) : null;
          const filters = this.selectedFilters.map(filter => judgeFilter(meals, this.nutrientsByMealId, filter, this.nutrients || {}, this.restrictions || {}));
          byDiet[dietId] = {dietId: Number(dietId), meals, rda, own, ownTargets, filters};
          if (rda.complete) {
            const judged = Number(dietId) === this.judgeDietId ? rda : judgeMeals(meals, this.nutrientsByMealId, this.judgeTargets);
            if (judged.judged) planned.push({met: judged.met, label: this.getDietName(dietId)});
          }
        }
        return {date, byDiet, swaps: this.swapsFor(grouped[date], planned, byDiet)};
      });
    },
    /** the planned days the server can judge: complete, of a diet with targets */
    plannedDays() {
      const days = [];
      for (const day of this.dayRows) {
        for (const entry of Object.values(day.byDiet)) {
          if (entry.rda.complete && this.dietHasTargets(entry.dietId)) {
            days.push({key: this.serverKey(day.date, entry), date: day.date, dietId: entry.dietId, mealIds: entry.meals.map(meal => meal.id)});
          }
        }
      }
      return days;
    },
    plannedDayKeys() {
      return this.plannedDays.map(day => day.key).join('|');
    },
    serverTotal() {
      return this.plannedDays.length;
    },
    serverDone() {
      return this.plannedDays.filter(day => this.serverChecks[day.key] && !this.serverChecks[day.key].pending).length;
    },
    serverPending() {
      return this.serverCheck && this.serverDone < this.serverTotal;
    },
    serverProgress() {
      return this.serverTotal ? (this.serverDone / this.serverTotal) * 100 : 0;
    },
    // data issues
    likelyLimits() {
      return targetsLikelyLimits(dietTargets(this.targetRows, FULL_NUTRITION_DIET, this.nutrients || {}));
    },
    untracked() {
      return this.meals.length ? untrackedTargets(dietTargets(this.targetRows, FULL_NUTRITION_DIET, this.nutrients || {}), this.nutrientsByMealId) : [];
    },
    mealsWithoutNutrients() {
      return this.meals.filter(meal => !(meal.nutrients && meal.nutrients[this.size]));
    },
    incompleteDays() {
      const days = [];
      for (const day of this.dayRows) {
        for (const entry of Object.values(day.byDiet)) {
          if (!entry.rda.complete) days.push({date: day.date, dietId: entry.dietId, dietName: this.getDietName(entry.dietId), count: entry.meals.length});
        }
      }
      return days;
    },
    /** selected filters whose nutrient no meal carries, or whose restriction has no definition */
    filterIssues() {
      const issues = [];
      for (const [index, filter] of this.selectedFilters.entries()) {
        const label = this.filterLabels[index];
        if (this.meals.length) {
          for (const target of untrackedTargets(filterTargets(filter, this.nutrients || {}), this.nutrientsByMealId)) {
            issues.push({key: `${filter.id}-${target.nutrient_id}`, text: `${label}: no meal in the menu carries ${target.name} (${target.symbol || 'no symbol'}), so every day fails it`});
          }
        }
        for (const id of restrictedMeals([], filter.restrictions, this.restrictions || {}).unknown) {
          issues.push({key: `${filter.id}-${id}`, text: `${label}: the restriction "${id}" has no definition or no tags, so it is not checked`});
        }
      }
      return issues;
    },
    issueCount() {
      return this.likelyLimits.length + this.untracked.length + this.mealsWithoutNutrients.length + this.streamsWithoutDiet.length
        + this.incompleteDays.length + this.filterIssues.length;
    },
  },
  methods: {
    ...mapActions(['fetchDiets', 'fetchNutrients', 'fetchRestrictions']),
    weekFrom(start) {
      return [start.format('YYYY-MM-DD'), start.clone().add(6, 'days').format('YYYY-MM-DD')];
    },
    skipWeek(direction) {
      const [from] = this.dates;
      if (!from) return;
      this.dates = this.weekFrom(moment(from).add(direction * 7, 'days'));
    },
    formatDate(date) {
      return moment(date).format('dddd D MMMM YYYY');
    },
    dietName(dietId) {
      return this.getDietName(dietId);
    },
    dietHasTargets(dietId) {
      return this.dietIdsWithTargets.includes(Number(dietId));
    },
    mealsAt(meals, tod) {
      return meals.filter(meal => meal.tod === tod);
    },
    mealLink(meal) {
      const date = meal.date ? String(meal.date).slice(0, 10) : null;
      return date ? {name: 'MealDetail', params: {date, id: String(meal.id)}} : {name: 'MealDesign', params: {id: String(meal.id)}};
    },
    pct(met, total) {
      return total ? (met / total) * 100 : null;
    },
    scoreColour(score) {
      if (score === null || score === undefined) return 'grey';
      if (score >= 90) return 'green';
      if (score >= 75) return 'light-green darken-1';
      if (score >= 50) return 'orange';
      return 'red';
    },
    scoreText(judged) {
      return judged.judged ? `${judged.met}/${judged.total}` : '?';
    },
    metTitle(judged) {
      return judged.judged
        ? `${judged.met} of ${judged.total} targets met (${Math.round(judged.score)}%), closeness ${Math.round(judged.closeness)}%`
        : 'the menu carries no nutrients for a meal of this day';
    },
    averageCloseness(dietId) {
      const values = this.dayRows
        .map(day => day.byDiet[dietId])
        .filter(entry => entry && entry.rda.judged)
        .map(entry => entry.rda.closeness);
      return values.length ? `${Math.round(values.reduce((sum, value) => sum + value, 0) / values.length)}%` : '';
    },
    /** a diet's days against the selected filter at index: days judged, days met, mean closeness */
    filterDays(dietId, index) {
      const judgements = this.dayRows
        .map(day => day.byDiet[dietId])
        .filter(entry => entry && entry.filters[index] && entry.filters[index].judged)
        .map(entry => entry.filters[index]);
      const met = judgements.filter(judgement => judgement.met).length;
      return {
        judged: judgements.length,
        met,
        metPercent: judgements.length ? (met / judgements.length) * 100 : null,
        closeness: judgements.length ? judgements.reduce((sum, judgement) => sum + judgement.closeness, 0) / judgements.length : null,
      };
    },
    filterDescription(filter) {
      const parts = [filter.description, ...filterTargets(filter, this.nutrients || {}).map(target => `${target.name} ${this.limits(target)}`)];
      if ((filter.restrictions || []).length) parts.push(`restrictions: ${filter.restrictions.map(this.restrictionText).join(', ')}`);
      return parts.filter(Boolean).join('\n');
    },
    restrictionText(id) {
      const restriction = (this.restrictions || {})[id];
      return (restriction && restriction.text) || id;
    },
    averageScore(dietId) {
      const scores = this.dayRows
        .map(day => day.byDiet[dietId])
        .filter(entry => entry && entry.rda.judged)
        .map(entry => entry.rda.score);
      return scores.length ? `${Math.round(scores.reduce((sum, score) => sum + score, 0) / scores.length)}%` : '';
    },
    problemColour(failure) {
      if (failure.problems.includes('over upper limit')) return 'red';
      if (failure.problems.includes('no data')) return 'grey';
      return 'orange darken-2';
    },
    decimals(target) {
      const nutrient = (this.nutrients || {})[target.nutrient_id];
      return nutrient && nutrient.nutrientdecimals !== null && nutrient.nutrientdecimals !== undefined ? Math.min(2, nutrient.nutrientdecimals) : 0;
    },
    describe(failure) {
      return describeFailure(failure, this.decimals(failure));
    },
    shortFailure(failure) {
      if (failure.problems.includes('no data')) return `${failure.name} (no data)`;
      if (failure.problems.includes('over upper limit')) return `${failure.name} over`;
      if (failure.percent !== null) return `${failure.name} ${Math.round(failure.percent)}%`;
      return `${failure.name} low`;
    },
    amount(value, target) {
      return value === null || value === undefined ? '' : `${Number(value).toFixed(this.decimals(target))}${target.unit}`;
    },
    limits(target) {
      const parts = [];
      if (target.lower) parts.push(`≥ ${this.amount(target.lower, target)}`);
      if (target.upper) parts.push(`≤ ${this.amount(target.upper, target)}`);
      return parts.join(' ');
    },
    /** the diet's own failures on nutrients the RDA check did not already flag */
    ownOnlyFailures(entry) {
      const rdaFailed = new Set(entry.rda.failures.map(failure => failure.nutrient_id));
      return entry.own.failures.filter(failure => !rdaFailed.has(failure.nutrient_id));
    },
    /** every RDA target's result with the diet's own result for nutrients where its target differs */
    detailRows(entry) {
      const own = entry.own ? Object.fromEntries(entry.own.results.map(result => [result.nutrient_id, result])) : {};
      const differing = entry.ownTargets ? new Set(differingTargets(entry.ownTargets, this.rdaTargets).map(target => target.nutrient_id)) : new Set();
      const rows = entry.rda.results.map(result => ({...result, own: differing.has(result.nutrient_id) ? own[result.nutrient_id] || null : null}));
      // the diet's own targets on nutrients the RDAs do not track
      const rdaIds = new Set(rows.map(row => row.nutrient_id));
      for (const result of entry.own ? entry.own.results : []) {
        if (!rdaIds.has(result.nutrient_id)) {
          rows.push({...result, target: null, lower: null, upper: null, percent: null, problems: [], met: true, own: result});
        }
      }
      return rows;
    },
    perfectShare(summary) {
      const perfect = summary.distribution[summary.total] ? summary.distribution[summary.total].count : 0;
      return `${perfect.toLocaleString()} (${Math.round((perfect / summary.count) * 100)}%)`;
    },
    problemSummary(failure) {
      return Object.entries(failure.problems).map(([problem, count]) => `${problem} ×${count.toLocaleString()}`).join(', ');
    },
    swapsFor(mealsByDiet, planned, byDiet) {
      const pool = new Set(this.poolDiets.map(Number));
      const meals = Object.entries(mealsByDiet)
        .filter(([dietId]) => pool.has(Number(dietId)))
        .flatMap(([, list]) => list);
      const poolNames = this.diets.filter(diet => pool.has(diet.id) && mealsByDiet[diet.id]).map(diet => diet.name);
      const {combos, count, sampled} = swapCombos(meals);
      const judged = judgeCombos(combos, this.nutrientsByMealId, this.judgeTargets);
      // every swap against each selected preset or goal, with the planned days for comparison
      const filters = this.selectedFilters.map((filter, index) => ({
        filter,
        label: this.filterLabels[index],
        summary: filterSwapSummary(combos.map(combo => judgeFilter(combo, this.nutrientsByMealId, filter, this.nutrients || {}, this.restrictions || {}))),
        planned: Object.values(byDiet)
          .filter(entry => entry.rda.complete && entry.filters[index].judged)
          .map(entry => ({label: this.getDietName(entry.dietId), met: entry.filters[index].met, closeness: entry.filters[index].closeness})),
      }));
      return {
        poolNames,
        count,
        sampled,
        summary: swapSummary(judged),
        planned,
        filters,
        worst: judged.slice(0, SHOWN_COMBOS),
        best: judged.slice(-SHOWN_COMBOS).reverse(),
      };
    },
    ignoreLikelyLimits() {
      const symbols = this.likelyLimits.map(target => target.symbol).filter(Boolean);
      this.ignored = [...new Set([...this.ignored, ...symbols])];
    },
    // the server check
    serverKey(date, entry) {
      return `${date}:${entry.dietId}:${entry.meals.map(meal => meal.id).sort((a, b) => a - b).join(',')}`;
    },
    serverCheckFor(date, entry) {
      if (!entry.rda.complete || !this.dietHasTargets(entry.dietId)) return null;
      return this.serverChecks[this.serverKey(date, entry)] || null;
    },
    /** the server judges with the diet's own targets, so compare with the diet's judgement (the RDAs when they are the same) */
    serverComparison(date, entry) {
      const check = this.serverCheckFor(date, entry);
      return compareWithServer(entry.own || entry.rda, check ? check.rows : []);
    },
    serverVerdictDiffers(date, entry) {
      const check = this.serverCheckFor(date, entry);
      return !!check && !check.pending && !check.error && this.serverComparison(date, entry).differences.some(difference => difference.verdictDiffers);
    },
    serverChipColour(comparison) {
      if (comparison.differences.some(difference => difference.verdictDiffers)) return 'red';
      if (comparison.differences.length) return 'orange darken-2';
      return 'green';
    },
    serverChipText(comparison) {
      const verdicts = comparison.differences.filter(difference => difference.verdictDiffers).length;
      if (verdicts) return `differs on ${verdicts}`;
      if (comparison.differences.length) return `${comparison.differences.length} amount${comparison.differences.length === 1 ? '' : 's'} differ`;
      return 'agrees';
    },
    async runServerChecks() {
      if (!this.serverCheck) return;
      const missing = this.plannedDays.filter(day => !this.serverChecks[day.key]);
      for (const day of missing) {
        this.$set(this.serverChecks, day.key, {pending: true, rows: [], error: null});
      }
      let next = 0;
      const worker = async () => {
        while (next < missing.length) {
          const day = missing[next++];
          try {
            const {data} = await api.get('meals/nutrients', {params: {mealIds: day.mealIds, dietId: day.dietId}});
            this.$set(this.serverChecks, day.key, {pending: false, rows: data, error: null});
          } catch (e) {
            const message = (e.response && e.response.data && e.response.data.error) || e.message || `${e}`;
            this.$set(this.serverChecks, day.key, {pending: false, rows: [], error: message});
          }
        }
      };
      await Promise.all(Array.from({length: Math.min(SERVER_CHECK_CONCURRENCY, missing.length)}, worker));
    },
    async fetchTargets() {
      const {data} = await api.get('nutrients/targets/');
      this.targetRows = data;
    },
    /** the meal filter definitions: diet presets, nutrition goals and the other filters */
    async fetchMealFilters() {
      const {data} = await api.get('v2/meal-filter');
      this.mealFilters = Array.isArray(data) ? data : [];
    },
    /** the published menu of every week the range touches, one request a week */
    async fetchMenus(from, to) {
      const weeks = [];
      for (let day = moment(from).startOf('week'); !day.isAfter(to); day.add(7, 'days')) {
        weeks.push(day.format('YYYY-MM-DD'));
      }
      const menus = await Promise.all(weeks.map(week => api.get(`v2/menu/${week}`).then(({data}) => data)));
      return menus.filter(Boolean);
    },
    async fetchData() {
      const [from, to = from] = this.dates || [];
      if (!from) return;
      this.loading = true;
      this.error = null;
      try {
        const [menus] = await Promise.all([
          this.fetchMenus(from, to), this.fetchDiets(), this.fetchNutrients(), this.fetchTargets(), this.fetchMealFilters(), this.fetchRestrictions(),
        ]);
        const {meals, streamsWithoutDiet} = menuMeals(menus, this.dietsById || {}, from, to);
        this.meals = meals;
        this.streamsWithoutDiet = streamsWithoutDiet;
        this.menusLoaded = true;
      } catch (e) {
        console.error('day nutrition load failed', e);
        this.error = `Could not load the menus: ${e.message}`;
        this.meals = [];
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.meal-cell {
  max-width: 220px;
}

.meal-cell a {
  display: block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.detail-row > td {
  padding: 0 0 12px 24px !important;
}

.detail-table {
  background: #fafafa;
}

.bounds-table td {
  height: 28px !important;
}

.filters-table {
  width: auto;
}

h4 {
  margin: 12px 0 4px;
}
</style>
